import type {
  AllSalaryCodesQuery,
  AllSalaryCodesQueryVariables,
  SalaryCodeNode,
} from '@/modules/companies'
import { ALL_SALARY_CODES_QUERY } from '@/modules/companies'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useDebounce, useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import type { SelectProps as BaseSelectProps } from './Select'
import { Select } from './Select'

interface SalaryCodeSelectProps
  extends Omit<BaseSelectProps<string, SalaryCodeNode>, 'options'> {
  alwaysInclude?: SalaryCodeNode
  isDiet?: boolean
  isSalaryDay?: boolean
  showInManualTimeEntryOvertime?: boolean
  isAddon?: boolean
  setOnLoad?: boolean
}

export const SalaryCodeSelect: React.FC<SalaryCodeSelectProps> = ({
  alwaysInclude = null,
  isDiet = undefined,
  isSalaryDay = undefined,
  showInManualTimeEntryOvertime = undefined,
  setOnLoad = false,
  isAddon = false,
  ...selectProps
}) => {
  const { placeholder } = useTranslate({
    placeholder: 'common.select-salary-code',
  })

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)
  const { data, loading, fetchMore } = useQuery<
    AllSalaryCodesQuery,
    AllSalaryCodesQueryVariables
  >(ALL_SALARY_CODES_QUERY, {
    variables: {
      q: debouncedQuery,
      isDiet: isDiet,
      isSalaryDay: isSalaryDay,
      isAddon: isAddon,
      showInManualTimeEntryOvertime: showInManualTimeEntryOvertime,
    },
    onCompleted: data => {
      const codes = data.allSalaryCodes.edges.map(({ node }) => node)
      const codeIds = codes.map(code => code.id)
      if (setOnLoad && codeIds.length >= 1 && !selectProps.value) {
        selectProps.onChange(codeIds[0] ?? null, null)
      }
    },
  })

  const getLabel = useCallback((salaryCode: SalaryCodeNode) => {
    return (
      <span className="name">
        {salaryCode.externalId
          ? `(${salaryCode.externalId}) ${salaryCode.name}`
          : salaryCode.name}
      </span>
    )
  }, [])

  const options = useMemo<SelectOption<string, SalaryCodeNode>[]>(() => {
    if (!data) return []

    const options = data.allSalaryCodes.edges.map(({ node: salaryCode }) => ({
      value: salaryCode.id,
      label: getLabel(salaryCode),
      extra: salaryCode,
    }))
    if (alwaysInclude === null) return options

    return [
      {
        value: alwaysInclude.id,
        label: getLabel(alwaysInclude),
        extra: alwaysInclude,
      },
      ...options.filter(option => option.extra.id !== alwaysInclude.id),
    ]
  }, [alwaysInclude, data, getLabel])

  /**
   * Whenever the user select gets close to the bottom, refetch.
   * @remarks Almost a complete copy of `handleScrollBottom` in `UsersTable.tsx`. Might want to refactor to a hook?
   */
  const handleFetchMore = useCallback(async () => {
    if (typeof data === 'undefined') return
    if (loading || data.allSalaryCodes.pageInfo?.endCursor === null) return
    try {
      await fetchMore({
        variables: {
          first: DEFAULT_PAGE_SIZE,
          q: debouncedQuery,
          orderBy: 'name',
          after: data.allSalaryCodes.pageInfo?.endCursor,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, fetchMore, debouncedQuery, loading])

  return (
    <Select
      options={options}
      loading={loading}
      placeholder={placeholder}
      searchable={true}
      filterBackground="white"
      optionHoverBackground="white"
      onFilterChange={setQuery}
      onFetchMore={handleFetchMore}
      {...selectProps}
    />
  )
}
